import { useEffect, useRef, useState, useMemo } from "react";
import { api, API } from "@/lib/api";
import {
  MessageSquare, Plus, Send, Hash, Lock, Loader2, X, AlertTriangle, Search,
  Paperclip, Smile, Reply, Users as UsersIcon, Mail, Trash2, ChevronDown,
  ExternalLink, MoreVertical, Check, CheckCheck,
} from "lucide-react";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";

const POLL_INTERVAL_MS = 5000;
const QUICK_EMOJIS = ["👍", "🙏", "🎯", "✅", "🚨", "💡"];

/**
 * Chat module — channel list (left), message thread (centre), member panel
 * (right). Polls /messages?after=<last> every 5 seconds for new messages.
 */
export default function Chat() {
  const { t } = useTranslation();
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [channelMeta, setChannelMeta] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creatingChannel, setCreatingChannel] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const scrollRef = useRef(null);
  const lastTsRef = useRef(null);

  const loadChannels = async () => {
    try {
      const r = await api.get("/workspace/chat/channels");
      const items = r.data.items || [];
      setChannels(items);
      if (!activeId && items.length > 0) setActiveId(items[0].id);
      return items;
    } catch (e) { setError(fmtError(e)); return []; }
  };
  const loadUsers = async () => {
    try { const r = await api.get("/workspace/chat/org-users"); setOrgUsers(r.data.items || []); }
    catch { /* non blocking */ }
  };

  const loadMessages = async (cid, isPoll = false) => {
    try {
      const params = isPoll && lastTsRef.current ? { after: lastTsRef.current } : {};
      const r = await api.get(`/workspace/chat/channels/${cid}/messages`, { params });
      const items = r.data.items || [];
      setChannelMeta(r.data.channel);
      if (isPoll) {
        if (items.length > 0) {
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            const fresh = items.filter((m) => !seen.has(m.id));
            return fresh.length ? [...prev, ...fresh] : prev;
          });
          lastTsRef.current = items[items.length - 1].created_at;
          api.post(`/workspace/chat/channels/${cid}/read`).catch(() => {});
        }
      } else {
        setMessages(items);
        if (items.length > 0) lastTsRef.current = items[items.length - 1].created_at;
        else lastTsRef.current = null;
        api.post(`/workspace/chat/channels/${cid}/read`).catch(() => {});
      }
    } catch (e) {
      if (!isPoll) setError(fmtError(e));
    }
  };

  // Initial load
  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadChannels(), loadUsers()]);
      setLoading(false);
    })();
  }, []);

  // When activeId changes → reload
  useEffect(() => {
    if (!activeId) return;
    setSearchResults(null);
    lastTsRef.current = null;
    loadMessages(activeId, false);
  }, [activeId]);

  // Polling
  useEffect(() => {
    if (!activeId) return;
    const t = setInterval(() => loadMessages(activeId, true), POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [activeId]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const activeChannel = useMemo(() => channelMeta || channels.find((c) => c.id === activeId), [channelMeta, channels, activeId]);

  const sendMessage = async (content, files = []) => {
    if (!activeId) return;
    const hasContent = content && content.trim();
    const hasFiles = files && files.length > 0;
    if (!hasContent && !hasFiles) return;
    try {
      // 1) Create the message (even if content is empty we still send a "[pièce jointe]" placeholder)
      const body = { content: hasContent ? content.trim() : (files.length === 1 ? files[0].name : `${files.length} pièces jointes`) };
      const r = await api.post(`/workspace/chat/channels/${activeId}/messages`, body);
      let msg = r.data;
      // 2) Upload attachments sequentially to the new message
      if (hasFiles) {
        for (const f of files) {
          if (f.size > 25 * 1024 * 1024) {
            notify.error(t("chat.too_large", { defaultValue: "Fichier trop volumineux (max 25 Mo)." }) + ` — ${f.name}`);
            continue;
          }
          const fd = new FormData();
          fd.append("file", f);
          try {
            const ar = await api.post(`/workspace/chat/channels/${activeId}/messages/${msg.id}/attachments`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
              timeout: 120000,
            });
            msg = { ...msg, attachments: [...(msg.attachments || []), ar.data] };
          } catch (e) {
            notify.error(`${f.name} — ${fmtError(e)}`);
          }
        }
      }
      setMessages((prev) => [...prev, msg]);
      lastTsRef.current = msg.created_at;
      loadChannels();
    } catch (e) { notify.error(fmtError(e)); }
  };

  const toggleReaction = async (mid, emoji) => {
    try {
      await api.post(`/workspace/chat/channels/${activeId}/messages/${mid}/reactions`, { emoji });
      // Optimistic refresh
      lastTsRef.current = null;
      loadMessages(activeId);
    } catch (e) { notify.error(fmtError(e)); }
  };

  const deleteChannel = async () => {
    if (!activeChannel) return;
    if (!window.confirm(t("chat.confirm_delete_channel", { defaultValue: "Supprimer ce canal et tous ses messages ?" }))) return;
    try {
      await api.delete(`/workspace/chat/channels/${activeId}`);
      notify.success(t("chat.toast_channel_deleted", { defaultValue: "Canal supprimé." }));
      setActiveId(null);
      await loadChannels();
    } catch (e) { notify.error(fmtError(e)); }
  };

  const runSearch = async () => {
    if (!searchQ.trim() || !activeId) { setSearchResults(null); return; }
    try {
      const r = await api.get(`/workspace/chat/channels/${activeId}/search`, { params: { q: searchQ.trim() } });
      setSearchResults(r.data.items || []);
    } catch (e) { notify.error(fmtError(e)); }
  };

  if (loading) {
    return <div className="text-zinc-400 text-sm p-6">{t("common.loading", { defaultValue: "Chargement…" })}</div>;
  }

  return (
    <div className="grid grid-cols-12 gap-3 h-[calc(100vh-140px)]" data-testid="chat-root">
      {/* Channels list */}
      <aside className="col-span-3 noam-card flex flex-col overflow-hidden">
        <div className="p-3 border-b border-white/5 flex items-center justify-between">
          <div className="noam-overline flex items-center gap-1.5">
            <MessageSquare className="w-3 h-3" /> {t("chat.channels", { defaultValue: "Canaux" })}
          </div>
          <button onClick={() => setCreatingChannel(true)}
            className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-white"
            data-testid="chat-create-channel-btn" title={t("chat.new_channel", { defaultValue: "Nouveau canal" })}>
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
        <ChannelList
          channels={channels}
          activeId={activeId}
          orgUsers={orgUsers}
          onSelect={setActiveId}
          onCreateDM={async (uid) => {
            try {
              const r = await api.post("/workspace/chat/channels/dm", { user_id: uid });
              await loadChannels();
              setActiveId(r.data.id);
            } catch (e) { notify.error(fmtError(e)); }
          }}
        />
      </aside>

      {/* Conversation */}
      <main className="col-span-9 noam-card flex flex-col overflow-hidden" data-testid="chat-main">
        {!activeChannel ? (
          <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
            {t("chat.empty_select", { defaultValue: "Sélectionnez un canal pour commencer." })}
          </div>
        ) : (
          <>
            <header className="p-3 border-b border-white/5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <ChannelIcon channel={activeChannel} />
                  <div className="text-sm font-semibold text-white truncate">{displayName(activeChannel, orgUsers)}</div>
                  {activeChannel.kind === "external" && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider bg-[#EF9F27]/15 text-[#EF9F27] border border-[#EF9F27]/40">
                      {t("chat.external_badge", { defaultValue: "Externe" })}
                    </span>
                  )}
                </div>
                {activeChannel.topic && (
                  <div className="text-[11px] text-zinc-500 mt-0.5 truncate">{activeChannel.topic}</div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-2 py-1 rounded border border-white/5">
                  <Search className="w-3 h-3 text-zinc-500" />
                  <input value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
                    placeholder={t("chat.search_placeholder", { defaultValue: "Rechercher…" })}
                    className="bg-transparent text-xs outline-none w-32" data-testid="chat-search-input" />
                </div>
                <button onClick={() => setShowMembers((v) => !v)} className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-white"
                  data-testid="chat-members-btn" title={t("chat.members", { defaultValue: "Membres" })}>
                  <UsersIcon className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => setShowInvite(true)} className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-white"
                  data-testid="chat-invite-btn" title={t("chat.invite_external", { defaultValue: "Inviter externe" })}>
                  <Mail className="w-3.5 h-3.5" />
                </button>
                {!activeChannel.is_default && (
                  <button onClick={deleteChannel} className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-[#E24B4A]"
                    data-testid="chat-delete-channel-btn" title={t("chat.delete_channel", { defaultValue: "Supprimer canal" })}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </header>

            {error && (
              <div className="flex items-start gap-2 p-2 m-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span>{error}</span>
                <button className="ml-auto" onClick={() => setError(null)}><X className="w-3 h-3" /></button>
              </div>
            )}

            {/* Messages or search results */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {searchResults !== null ? (
                <>
                  <div className="noam-overline text-zinc-500">
                    {t("chat.search_results", { defaultValue: "Résultats" })} ({searchResults.length})
                    <button onClick={() => { setSearchResults(null); setSearchQ(""); }} className="ml-2 underline">
                      {t("chat.search_close", { defaultValue: "Fermer" })}
                    </button>
                  </div>
                  {searchResults.map((m) => <MessageBubble key={m.id} msg={m} onReact={() => {}} />)}
                  {searchResults.length === 0 && <div className="text-zinc-500 text-xs italic">{t("chat.no_results", { defaultValue: "Aucun résultat." })}</div>}
                </>
              ) : (
                <>
                  {messages.length === 0 && (
                    <div className="text-zinc-500 text-xs italic text-center mt-12">
                      {t("chat.empty_messages", { defaultValue: "Aucun message. Écrivez le premier." })}
                    </div>
                  )}
                  {groupByDay(messages).map((grp) => (
                    <div key={grp.day}>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-600 text-center my-3">
                        {formatDay(grp.day)}
                      </div>
                      {grp.items.map((m, idx) => {
                        const prev = idx > 0 ? grp.items[idx - 1] : null;
                        const sameSender = prev && prev.sender_id === m.sender_id;
                        return <MessageBubble key={m.id} msg={m} compact={sameSender}
                          onReact={(emoji) => toggleReaction(m.id, emoji)} />;
                      })}
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Composer */}
            <Composer onSend={sendMessage} />
          </>
        )}
      </main>

      {creatingChannel && (
        <NewChannelModal
          orgUsers={orgUsers}
          onClose={() => setCreatingChannel(false)}
          onCreated={async (id) => { setCreatingChannel(false); await loadChannels(); setActiveId(id); }}
        />
      )}
      {showInvite && activeChannel && (
        <InviteExternalModal channel={activeChannel} onClose={() => setShowInvite(false)} />
      )}
      {showMembers && activeChannel && (
        <MembersPanel channel={activeChannel} orgUsers={orgUsers}
          onClose={() => setShowMembers(false)}
          onUpdated={async () => { await loadChannels(); await loadMessages(activeId); }}
        />
      )}
      <ImageLightbox />
    </div>
  );
}

// ---------------- ImageLightbox ----------------
function ImageLightbox() {
  const [state, setState] = useState(null); // {src, filename} | null
  useEffect(() => {
    const onOpen = (e) => setState(e.detail || null);
    const onKey = (e) => { if (e.key === "Escape") setState(null); };
    window.addEventListener("chat:open-lightbox", onOpen);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("chat:open-lightbox", onOpen);
      window.removeEventListener("keydown", onKey);
    };
  }, []);
  if (!state) return null;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => setState(null)}
      data-testid="chat-image-lightbox"
    >
      <button
        onClick={(e) => { e.stopPropagation(); setState(null); }}
        className="absolute top-4 right-4 text-zinc-300 hover:text-white p-2 rounded-md bg-white/5 border border-white/10"
        data-testid="chat-lightbox-close"
        aria-label="Fermer"
      >
        <X className="w-4 h-4" />
      </button>
      <a
        href={state.src}
        download={state.filename}
        onClick={(e) => e.stopPropagation()}
        className="absolute top-4 right-16 text-zinc-300 hover:text-white text-xs px-3 py-2 rounded-md bg-white/5 border border-white/10"
        data-testid="chat-lightbox-download"
      >
        {state.filename}
      </a>
      <img
        src={state.src}
        alt={state.filename}
        className="max-h-[90vh] max-w-[90vw] object-contain rounded shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

// ---------------- ChannelList ----------------
function ChannelList({ channels, activeId, orgUsers, onSelect, onCreateDM }) {
  const { t } = useTranslation();
  const grouped = useMemo(() => {
    const out = { internal: [], dm: [], external: [] };
    channels.forEach((c) => {
      const k = c.kind === "dm" ? "dm" : c.kind === "external" ? "external" : "internal";
      out[k].push(c);
    });
    return out;
  }, [channels]);
  return (
    <div className="flex-1 overflow-y-auto p-2 space-y-3">
      <Section title={t("chat.section_channels", { defaultValue: "Canaux" })} items={grouped.internal} activeId={activeId} onSelect={onSelect} orgUsers={orgUsers} testidPrefix="chat-channel" />
      <Section title={t("chat.section_external", { defaultValue: "Externes" })} items={grouped.external} activeId={activeId} onSelect={onSelect} orgUsers={orgUsers} testidPrefix="chat-external" />
      <Section title={t("chat.section_dms", { defaultValue: "Messages directs" })} items={grouped.dm} activeId={activeId} onSelect={onSelect} orgUsers={orgUsers} testidPrefix="chat-dm" />
      <DMPicker orgUsers={orgUsers} onPick={onCreateDM} />
    </div>
  );
}

function Section({ title, items, activeId, onSelect, orgUsers, testidPrefix }) {
  if (items.length === 0) return null;
  return (
    <div>
      <div className="noam-overline text-zinc-500 px-2 mb-1">{title}</div>
      {items.map((c) => (
        <button key={c.id} onClick={() => onSelect(c.id)}
          data-testid={`${testidPrefix}-${c.id}`}
          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-xs transition group ${
            activeId === c.id ? "bg-[#378ADD]/15 text-white" : "text-zinc-300 hover:bg-white/5"
          }`}>
          <ChannelIcon channel={c} small />
          <span className="flex-1 truncate">{displayName(c, orgUsers)}</span>
          {c.unread_count > 0 && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#E24B4A] text-white font-bold">
              {c.unread_count > 99 ? "99+" : c.unread_count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

function DMPicker({ orgUsers, onPick }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen((v) => !v)}
        className="w-full text-[11px] text-zinc-500 hover:text-zinc-200 px-2 py-1.5 inline-flex items-center gap-1"
        data-testid="chat-dm-picker-toggle">
        <Plus className="w-3 h-3" /> {t("chat.start_dm", { defaultValue: "Démarrer un DM" })}
        <ChevronDown className={`w-3 h-3 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-0.5 mt-1 max-h-48 overflow-y-auto">
          {orgUsers.map((u) => (
            <button key={u.id} onClick={() => { onPick(u.id); setOpen(false); }}
              data-testid={`chat-dm-pick-${u.id}`}
              className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-white/5 text-zinc-300 truncate">
              {u.name || u.email}
            </button>
          ))}
          {orgUsers.length === 0 && <div className="px-2 py-1.5 text-[11px] text-zinc-600 italic">{t("chat.no_org_users", { defaultValue: "Aucun autre utilisateur." })}</div>}
        </div>
      )}
    </div>
  );
}

// ---------------- MessageBubble ----------------
function MessageBubble({ msg, onReact, compact }) {
  const { t } = useTranslation();
  const [showActions, setShowActions] = useState(false);
  const [showEmojis, setShowEmojis] = useState(false);
  const reactionsByEmoji = useMemo(() => {
    const out = {};
    (msg.reactions || []).forEach((r) => {
      out[r.emoji] = (out[r.emoji] || 0) + 1;
    });
    return Object.entries(out);
  }, [msg.reactions]);
  return (
    <div className={`group relative ${compact ? "" : "mt-2"}`}
      onMouseEnter={() => setShowActions(true)} onMouseLeave={() => { setShowActions(false); setShowEmojis(false); }}
      data-testid={`chat-msg-${msg.id}`}>
      <div className="flex items-start gap-2">
        <div className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-[10px] font-bold ${compact ? "invisible" : ""}`}
          style={{ background: stringToColor(msg.sender_id) }}>
          {(msg.sender_name || "?").slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          {!compact && (
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-semibold text-zinc-100">{msg.sender_name || "Membre"}</span>
              <span className="text-[10px] text-zinc-500">{formatTime(msg.created_at)}</span>
            </div>
          )}
          <div className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap break-words">{msg.content}</div>
          {/* Attachments */}
          {msg.attachments?.length > 0 && (
            <div className="mt-1.5 space-y-1.5">
              {msg.attachments.map((a) => {
                const isImage = (a.content_type || "").toLowerCase().startsWith("image/");
                const href = `${API}/workspace/chat/attachments/${a.id}`;
                if (isImage) {
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => window.dispatchEvent(new CustomEvent("chat:open-lightbox", { detail: { src: href, filename: a.filename } }))}
                      className="block max-w-xs rounded-md overflow-hidden border border-white/10 hover:border-[#378ADD]/40 transition cursor-zoom-in bg-black/40"
                      data-testid={`chat-image-preview-${a.id}`}
                      title={a.filename}
                    >
                      <img
                        src={href}
                        alt={a.filename}
                        loading="lazy"
                        className="block max-h-64 w-auto object-contain"
                      />
                      <span className="block text-[10px] text-zinc-500 px-2 py-1 text-left truncate">
                        {a.filename} <span className="text-zinc-600">· {Math.round((a.size || 0) / 1024)} Ko</span>
                      </span>
                    </button>
                  );
                }
                return (
                  <a key={a.id} href={href}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded bg-white/5 border border-white/10 text-zinc-200 hover:bg-white/10">
                    <Paperclip className="w-3 h-3" /> {a.filename}
                    <span className="text-zinc-500">({Math.round((a.size || 0) / 1024)} Ko)</span>
                  </a>
                );
              })}
            </div>
          )}
          {/* Reactions */}
          {reactionsByEmoji.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {reactionsByEmoji.map(([emoji, count]) => (
                <button key={emoji} onClick={() => onReact(emoji)}
                  className="text-[11px] px-1.5 py-0.5 rounded-full bg-white/5 border border-white/10 hover:border-[#378ADD]/40">
                  {emoji} {count}
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Hover actions */}
        {showActions && (
          <div className="absolute -top-3 right-3 flex items-center gap-0.5 bg-[#0a0a0a] border border-white/10 rounded shadow-lg p-0.5 z-10">
            <button onClick={() => setShowEmojis((v) => !v)} className="p-1 rounded hover:bg-white/5 text-zinc-400 hover:text-white" data-testid={`chat-msg-react-${msg.id}`}>
              <Smile className="w-3 h-3" />
            </button>
            {showEmojis && (
              <div className="absolute top-full right-0 mt-1 bg-[#0a0a0a] border border-white/10 rounded shadow-lg p-1 flex gap-0.5">
                {QUICK_EMOJIS.map((e) => (
                  <button key={e} onClick={() => { onReact(e); setShowEmojis(false); }} className="px-1 hover:bg-white/5 rounded">
                    {e}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- Composer ----------------
function Composer({ onSend }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  const MAX_BYTES = 25 * 1024 * 1024;

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    const okFiles = [];
    for (const f of picked) {
      if (f.size > MAX_BYTES) {
        notify.error(`${f.name} — ${t("chat.too_large", { defaultValue: "Fichier trop volumineux (max 25 Mo)." })}`);
        continue;
      }
      okFiles.push(f);
    }
    setFiles((prev) => [...prev, ...okFiles]);
    // Reset the input so the same file can be picked again later
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (busy) return;
    if (!text.trim() && files.length === 0) return;
    setBusy(true);
    try {
      await onSend(text, files);
      setText("");
      setFiles([]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="border-t border-white/5 p-3 flex flex-col gap-2">
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="chat-composer-attachments">
          {files.map((f, idx) => (
            <span key={idx} className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded bg-white/5 border border-white/10 text-zinc-200">
              <Paperclip className="w-3 h-3" />
              <span className="max-w-[180px] truncate">{f.name}</span>
              <span className="text-zinc-500">({Math.round(f.size / 1024)} Ko)</span>
              <button type="button" onClick={() => removeFile(idx)}
                className="text-zinc-400 hover:text-red-300 ml-1"
                aria-label={t("chat.remove_attachment", { defaultValue: "Retirer" })}
                data-testid={`chat-composer-remove-${idx}`}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={onPick}
          className="hidden"
          data-testid="chat-composer-file-input"
        />
        <button type="button" onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          title={t("chat.attach_file", { defaultValue: "Joindre un fichier (max 25 Mo)" })}
          className="p-2 rounded-md border border-white/10 text-zinc-300 hover:bg-white/5 hover:text-zinc-100 disabled:opacity-50"
          data-testid="chat-composer-attach">
          <Paperclip className="w-3.5 h-3.5" />
        </button>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) submit(e); }}
          rows={1}
          disabled={busy}
          placeholder={t("chat.composer_placeholder", { defaultValue: "Écrivez un message…" })}
          className="noam-input flex-1 resize-none max-h-32 disabled:opacity-60"
          data-testid="chat-composer-input"
        />
        <button type="submit" disabled={busy || (!text.trim() && files.length === 0)}
          className="btn-primary text-xs disabled:opacity-50"
          data-testid="chat-composer-send">
          {busy
            ? <span className="text-[11px]">{t("chat.sending", { defaultValue: "Envoi…" })}</span>
            : <Send className="w-3.5 h-3.5" />}
        </button>
      </div>
    </form>
  );
}

// ---------------- Modals ----------------
function NewChannelModal({ orgUsers, onClose, onCreated }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await api.post("/workspace/chat/channels", {
        name: name.trim(), topic: topic.trim(), kind: "internal", member_ids: selectedIds,
      });
      notify.success(t("chat.toast_channel_created", { defaultValue: "Canal créé." }));
      onCreated(r.data.id);
    } catch (e) { setErr(fmtError(e)); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell onClose={onClose} title={t("chat.new_channel", { defaultValue: "Nouveau canal" })} testid="chat-new-channel-modal">
      <form onSubmit={submit} className="p-5 space-y-3">
        <Field label={t("chat.channel_name", { defaultValue: "Nom" })}>
          <input required autoFocus value={name} onChange={(e) => setName(e.target.value)}
            placeholder="#risques" className="noam-input" data-testid="chat-new-channel-name" />
        </Field>
        <Field label={t("chat.channel_topic", { defaultValue: "Sujet" })}>
          <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="…" className="noam-input" />
        </Field>
        <Field label={t("chat.add_members", { defaultValue: "Ajouter des membres" })}>
          <div className="max-h-40 overflow-y-auto border border-white/10 rounded p-2 space-y-0.5">
            {orgUsers.map((u) => (
              <label key={u.id} className="flex items-center gap-2 px-1.5 py-1 hover:bg-white/5 rounded text-xs cursor-pointer">
                <input type="checkbox" checked={selectedIds.includes(u.id)}
                  onChange={(e) => setSelectedIds((prev) => e.target.checked ? [...prev, u.id] : prev.filter((x) => x !== u.id))} />
                <span className="text-zinc-300">{u.name || u.email}</span>
              </label>
            ))}
          </div>
        </Field>
        {err && (
          <div className="flex items-start gap-2 p-2 rounded border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {err}
          </div>
        )}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
          <button type="button" onClick={onClose} className="btn-ghost text-xs">{t("common.cancel", { defaultValue: "Annuler" })}</button>
          <button type="submit" disabled={busy} className="btn-primary text-xs" data-testid="chat-new-channel-submit">
            {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> …</> : t("common.create", { defaultValue: "Créer" })}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function InviteExternalModal({ channel, onClose }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/workspace/chat/channels/${channel.id}/invite`, {
        email: email.trim(), display_name: name.trim(), expires_days: 30,
      });
      setResult(r.data);
      notify.success(t("chat.toast_invite_created", { defaultValue: "Invitation créée." }));
    } catch (e) { setErr(fmtError(e)); }
    finally { setBusy(false); }
  };
  const fullUrl = result ? `${window.location.origin}${result.access_url_suffix}` : "";
  return (
    <ModalShell onClose={onClose} title={t("chat.invite_external", { defaultValue: "Inviter un externe" })} testid="chat-invite-modal">
      {!result ? (
        <form onSubmit={submit} className="p-5 space-y-3">
          <p className="text-xs text-zinc-400">
            {t("chat.invite_help", { defaultValue: "L'invité reçoit un lien donnant accès UNIQUEMENT à ce canal. Il ne voit pas le reste du workspace." })}
          </p>
          <Field label={t("chat.invite_email", { defaultValue: "Email" })}>
            <input required type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} className="noam-input" data-testid="chat-invite-email" />
          </Field>
          <Field label={t("chat.invite_name", { defaultValue: "Nom à afficher" })}>
            <input value={name} onChange={(e) => setName(e.target.value)} className="noam-input" />
          </Field>
          {err && <div className="text-[#FCA5A5] text-xs">{err}</div>}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
            <button type="button" onClick={onClose} className="btn-ghost text-xs">{t("common.cancel", { defaultValue: "Annuler" })}</button>
            <button type="submit" disabled={busy} className="btn-primary text-xs" data-testid="chat-invite-submit">
              {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> …</> : t("chat.send_invite", { defaultValue: "Émettre l'invitation" })}
            </button>
          </div>
        </form>
      ) : (
        <div className="p-5 space-y-3" data-testid="chat-invite-result">
          <div className="text-xs text-zinc-300">{t("chat.invite_url_label", { defaultValue: "Partagez ce lien à l'invité (valable 30 jours) :" })}</div>
          <div className="p-2 rounded bg-white/5 border border-white/10 text-xs text-[#B5D4F4] break-all select-all">{fullUrl}</div>
          <button onClick={() => navigator.clipboard.writeText(fullUrl)} className="btn-ghost text-xs">{t("chat.copy_link", { defaultValue: "Copier le lien" })}</button>
          <div className="text-[10px] text-zinc-500 italic">
            {t("chat.invite_email_note", { defaultValue: "L'envoi par email automatique sera activé une fois la passerelle Resend connectée." })}
          </div>
        </div>
      )}
    </ModalShell>
  );
}

function MembersPanel({ channel, orgUsers, onClose, onUpdated }) {
  const { t } = useTranslation();
  const [members, setMembers] = useState(channel.member_ids || []);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/workspace/chat/channels/${channel.id}`, { member_ids: members });
      notify.success(t("chat.toast_members_updated", { defaultValue: "Membres mis à jour." }));
      onUpdated();
      onClose();
    } catch (e) { notify.error(fmtError(e)); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell onClose={onClose} title={t("chat.members", { defaultValue: "Membres" })} testid="chat-members-modal">
      <div className="p-5 space-y-3">
        <div className="max-h-72 overflow-y-auto border border-white/10 rounded p-2 space-y-0.5">
          {orgUsers.map((u) => (
            <label key={u.id} className="flex items-center gap-2 px-1.5 py-1 hover:bg-white/5 rounded text-xs cursor-pointer">
              <input type="checkbox" checked={members.includes(u.id)}
                onChange={(e) => setMembers((prev) => e.target.checked ? [...prev, u.id] : prev.filter((x) => x !== u.id))} />
              <span className="text-zinc-300">{u.name || u.email}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
          <button type="button" onClick={onClose} className="btn-ghost text-xs">{t("common.cancel", { defaultValue: "Annuler" })}</button>
          <button onClick={save} disabled={busy} className="btn-primary text-xs" data-testid="chat-members-save">
            {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> …</> : t("common.save", { defaultValue: "Enregistrer" })}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

// ---------------- Helpers ----------------
function ChannelIcon({ channel, small }) {
  const cls = small ? "w-3 h-3" : "w-4 h-4";
  if (channel.kind === "dm") return <MessageSquare className={`${cls} text-[#7F77DD]`} />;
  if (channel.kind === "external") return <Mail className={`${cls} text-[#EF9F27]`} />;
  return <Hash className={`${cls} text-zinc-400`} />;
}

function displayName(channel, orgUsers) {
  if (channel.kind === "dm") {
    return channel.display_name || "DM";
  }
  return channel.name;
}

function stringToColor(s) {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  const hue = Math.abs(h) % 360;
  return `hsl(${hue}, 45%, 35%)`;
}

function formatTime(iso) {
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

function formatDay(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" });
  } catch { return iso; }
}

function groupByDay(messages) {
  const out = [];
  let current = null;
  messages.forEach((m) => {
    const day = (m.created_at || "").slice(0, 10);
    if (!current || current.day !== day) {
      current = { day, items: [] };
      out.push(current);
    }
    current.items.push(m);
  });
  return out;
}

function ModalShell({ children, onClose, title, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-start justify-center overflow-y-auto p-4 py-12" onClick={onClose}>
      <div className="noam-card w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div className="noam-display text-base font-semibold">{title}</div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">{label}</label>
      {children}
    </div>
  );
}
